import { useState } from 'react';
import { LuInfo, LuPlus } from 'react-icons/lu';

import { FeatureFlag } from '@ndvr/auth-api';

import { Button } from '@/components/ui/button';
import { Page, PageContent, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { useHasConnectedCalendar, useIsSyncingCalendar, useIsZoomConnected } from 'views/calendar/calendar-queries';

import { useEventCalendarViewState } from './calendar-state';
import { CalendarMeetingMinutesView } from './widgets/CalendarMeetingMinutesView';
import { CalendarPageHeader } from './widgets/CalendarPageHeader';
import { CalendarTabs } from './widgets/CalendarTabs';
import { ConnectCalendarCard } from './widgets/ConnectCalendarCard';
import { ConnectZoomButton } from './widgets/ConnectZoomButton';
import { EventCalendar } from './widgets/EventCalendar';
import { NewEventSheet } from './widgets/NewEventSheet';

export const CalendarPage = () => {
    const [isNewEventSheetOpen, setIsNewEventSheetOpen] = useState(false);
    const [anchorDate, setAnchorDate] = useState(() => new Date());
    const { eventCalendarViewState, setEventCalendarViewState } = useEventCalendarViewState();
    const activeTab = eventCalendarViewState.activeTab;
    const hasConnectedCalendar = useHasConnectedCalendar();
    const zoomConnected = useIsZoomConnected();
    const isSyncingCalendar = useIsSyncingCalendar();
    const calendarNotesEnabled = useFeatureFlag(FeatureFlag.CC_CALENDAR_NOTES);

    const newEventButton = (
        <Button variant="outline2" size="sm" className="px-3" onClick={() => setIsNewEventSheetOpen(true)}>
            <LuPlus className="h-4 w-4" /> Event
        </Button>
    );

    return (
        <Page>
            <PageTopBar />
            {hasConnectedCalendar.data !== true && <PageTitleRow title="Calendar" actions={newEventButton} />}
            <PageContent
                scroll="children"
                topPadding={hasConnectedCalendar.data === true ? 'none' : 'default'}
                className="gap-6"
            >
                {hasConnectedCalendar.data === false && <ConnectCalendarCard />}
                {hasConnectedCalendar.data === true && zoomConnected.data === false && <ConnectZoomButton />}
                {hasConnectedCalendar.data === true && isSyncingCalendar.data === true && (
                    <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                        <LuInfo className="size-4 shrink-0" />
                        <span>
                            Your calendar was recently connected and is still syncing events. Some events may not appear
                            yet — check back shortly.
                        </span>
                    </div>
                )}
                {hasConnectedCalendar.data === true &&
                    (calendarNotesEnabled ? (
                        <>
                            <div>
                                <CalendarPageHeader
                                    anchorDate={anchorDate}
                                    onAnchorDateChange={setAnchorDate}
                                    onNewEvent={() => setIsNewEventSheetOpen(true)}
                                />
                                <CalendarTabs
                                    activeTab={activeTab}
                                    onTabChange={(tab) => setEventCalendarViewState({ activeTab: tab })}
                                />
                            </div>
                            {activeTab === 'meetingMinutes' ? (
                                <CalendarMeetingMinutesView anchorDate={anchorDate} />
                            ) : (
                                <EventCalendar anchorDate={anchorDate} />
                            )}
                        </>
                    ) : (
                        <EventCalendar actions={newEventButton} />
                    ))}
            </PageContent>
            <NewEventSheet open={isNewEventSheetOpen} onOpenChange={setIsNewEventSheetOpen} />
        </Page>
    );
};
